import { useOutletContext, useParams } from "react-router-dom";
import { InspectionsPageContext } from "../InspectionsPage";
import { BlockButton, ODataGrid, SimpleCard, TextWithValidation } from "../../../components";
import { useEffect, useRef, useState } from "react";
import { InspectionService } from "../../../services";
import { GridColumn } from "@progress/kendo-react-grid";
import { useSharedState } from "../../../app/sharedProperty";
import { CompositeFilterDescriptor, filterBy } from "@progress/kendo-data-query";
import { NormalizeModal } from "./components";
import ProcessRepo from "../../../data/repo/ProcessRepo";
import { InspectionProcessModel } from "../../../data/entities/InspectionProcessModel";

export function InspectionsProcessNormalize() {
  const context: InspectionsPageContext = useOutletContext();
  context.setTitle("Process Normalize");

  const params = useParams();
  const id = params["id"] ?? "";
  const inspectionService = new InspectionService();
  const getPoints = (oDataQuery: string) =>
    inspectionService.getNormalizeDataPoints(Number(id));
  const [processes, setProcesses] = useState<InspectionProcessModel[]>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const loadedData = await new ProcessRepo().getProcessNormalize(Number(id));
        setProcesses(loadedData.processes);
      } catch (e) {
        console.error("Error loading data points", e);
      }
    };
    fetchData();
  }, [id])

  const initialFilter: CompositeFilterDescriptor = {
    logic: 'and',
    filters: [
      { field: "startPosition", operator: 'gte', value: 0 },
      { field: "startPosition", operator: 'lte', value: 0 }
    ],
  };

  const gridData = useSharedState<any>([]);
  const gridRef = useRef<ODataGrid>(null);
  const showDeleteState = useSharedState(false);
  const [filter, setFilter] = useState(initialFilter);
  const [range, setRange] = useState(() => {
    return {
      startPosition: 0,
      endPosition: 0
    }
  });

  return (
    <>

      <SimpleCard className="mb-3 p-0" title="Normalize data points.">
        <div className="row">
          <div className="col-12">
            <p>This data process allows you to split a segment in two, specifying the exact data point number and what position (in metres) that data point is at. For example, if you know that data point 1234 has a spike that was at exactly 302m, you can split the data at that point to increase the accuracy of the "Positions" data processing</p>
            <p>After you split the data, you should always do the "Positions" data processing again to recalculate the estimated position of the data points on either side of the split.</p>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-lg-6">
            <TextWithValidation
              id="startPosition"
              type="number"
              label="Start Position (m)"
              initialValue={range.startPosition.toString()}
              onChangeText={(value) => {
                setRange({
                  ...range,
                  startPosition: Number(value)
                })
              }}
              validationResult={(value) => {
                setFilter({
                  ...filter,
                  filters: [
                    { field: "startPosition", operator: 'gte', value: Number(value) },
                    { field: "startPosition", operator: 'lte', value: range.endPosition }
                  ]
                })
              }}
            />
          </div>
          <div className="col-12 col-lg-6">
            <TextWithValidation
              id="endPosition"
              type="number"
              label="End Position (m)"
              initialValue={range.endPosition.toString()}
              onChangeText={(value) => {
                setRange({
                  ...range,
                  endPosition: Number(value)
                })
              }}
              validationResult={(value) => {
                setFilter({
                  logic: 'and',
                  filters: [
                    { field: "startPosition", operator: 'gte', value: range.startPosition },
                    { field: "startPosition", operator: 'lte', value: Number(value) }
                  ],
                })
              }}
            />
          </div>
        </div>
        <div className="row">
          <div className="col col-12">
            <ODataGrid
              ref={gridRef}
              getData={getPoints}
              data={filterBy(gridData.value, filter)}
              dataState={gridData}
              sort={[{ field: "dataPointIndexByInspection", dir: "asc" }]}
              sortable={true}
              filterable={false}
              pageable={false}
              style={{ height: "350px" }}
            >
              <GridColumn title="Index" field="dataPointIndexByInspection" />
              <GridColumn title="Pos" field="startPosition" />
              <GridColumn title="FLW" field="flw" />
              <GridColumn title="Flaw" field="flaw" />
              <GridColumn title="Wall1" field="wall1" />
              <GridColumn title="Wall2" field="wall2" />
            </ODataGrid>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12 col-md-5 col-xl-4 col-xxl-3">
            <BlockButton
              label="Normalize"
              onClick={() => {
                showDeleteState.setter(true);
              }}
              disabled={false}
            />
            <NormalizeModal
              showState={showDeleteState}
              size="lg"
              title={`Set normalization variance`}
              setProcesses={setProcesses}
              range={range}
              confirmText="Submit" 
              id={Number(id)}
              />
              
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-12">Data Point Count: {filterBy(gridData.value, filter).length}</div>
        </div>
      </SimpleCard>

      <SimpleCard className="mb-3 p-0" title="Normalization history.">
        <table className="table hover-row mt-3">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {processes?.map((process) => {
                const createdDate = process?.createdDate
                  ? new Date(process.createdDate)
                  : null;
                const formattedDate = createdDate
                  ? `${createdDate.toLocaleDateString()} ${createdDate.toLocaleTimeString()}`
                  : 'N/A';
                return (
                  <tr key={`${process.id}`}>
                    <td style={{width: "180px"}}>
                      <span>{formattedDate}</span>
                    </td>
                    <td>
                      <span>{process.description}</span>
                    </td>
                  </tr>
                )
              })
            }
          </tbody>
        </table>
      </SimpleCard>

    </>
  );
}

export default InspectionsProcessNormalize;

